import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';

/**
 * @constant
 * @description The query param behavior for the utility function getNewQueryParamString
 * @type {Object}
 * @property {string} APPEND -  Append the new query param value to the existing query param value and de-dupe. This is the default behavior.
 * @example Input: "color: red", Before: "?color=black", After: "?color=black,red"
 *
 * @property {string} REPLACE - Replace the existing query param value and remove the query param if the new value is null.
 * @example Input: "color: red", Before: "?color=black", After: "?color=red"
 *
 * @property {string} TOGGLE - Toggle the existing query param value and remove the query param if the new value is null.
 * @example Input: "color: black", Before: "?color=black", After: "?"
 */
export enum QueryParamBehavior {
  APPEND = 'APPEND',
  REPLACE = 'REPLACE',
  TOGGLE = 'TOGGLE',
}

/**
 * By default the query params are not replaced, they are appended to the existing query params
 *
 * @param {string} params.query The query string from the URL
 * @param {Object} params.newQueryParams The new query params to replace/append to the existing query params
 * @param {QueryParamBehavior} params.queryParamBehavior The query param behavior to use. Default is QueryParamBehavior.APPEND
 * @returns {string} The new query string
 */
export const getNewQueryParamString = ({
  query,
  newQueryParams,
  queryParamBehavior = QueryParamBehavior.APPEND,
}: {
  query: string;
  newQueryParams: Record<string, string | string[]>;
  queryParamBehavior?: QueryParamBehavior;
}): string => {
  const params = new URLSearchParams(query);
  Object.entries(newQueryParams).forEach(([key, value]) => {
    const mappedKey = cioFacetsToUrlFilters[key] || key;

    // Overwrite the existing query param value if it exists
    // If the new value is an empty array or null, remove the query param
    // This is especially necessary for handling MobileGridFilters, where it needs to keep track of internal state
    // and is intentional about passing in null values to remove the query param
    if (queryParamBehavior === QueryParamBehavior.REPLACE) {
      if (!value?.length) {
        return params.delete(mappedKey);
      }

      return params.set(
        mappedKey,
        Array.isArray(value) ? value.join(',') : value
      );
    }

    // Add the new query param value to the existing query param value
    // or create a new query param if it doesn't exist
    // If the new query param value already exists, remove it
    // If there are no remaining values, remove the query param
    if (queryParamBehavior === QueryParamBehavior.TOGGLE) {
      const existingValue = params.get(mappedKey);
      const isValueArray = Array.isArray(value);
      const newValues = existingValue?.split(',') || [];

      if (isValueArray) {
        // If the value is an array, loop through it and toggle each value
        value.forEach((val) => {
          if (newValues.includes(val)) {
            newValues.splice(newValues.indexOf(val), 1);
          } else {
            newValues.push(val);
          }
        });
      } else {
        // If the value is not an array, just toggle it
        if (newValues.includes(value)) {
          newValues.splice(newValues.indexOf(value), 1);
        } else {
          newValues.push(value);
        }
      }

      // If the new values are empty, remove the query param
      if (newValues.length) {
        return params.set(mappedKey, newValues.join(','));
      } else {
        return params.delete(mappedKey);
      }
    }

    // Add the new query param value to the existing query param value,
    // or create a new query param if it doesn't exist
    // If the new query param value already exists, de-duplicate it
    // If there are no remaining values, remove the query param
    if (queryParamBehavior === QueryParamBehavior.APPEND) {
      const existingValue = params.get(mappedKey);
      const isValueArray = Array.isArray(value);
      const newValues = existingValue?.split(',') || [];

      if (isValueArray) {
        // If the value is an array, loop through it and add each value
        value.forEach((val) => {
          if (!newValues.includes(val)) {
            newValues.push(val);
          }
        });
      } else {
        // If the value is not an array, just add it
        if (!newValues.includes(value)) {
          newValues.push(value);
        }
      }
      // If the new values are empty, remove the query param
      if (newValues.length) {
        return params.set(mappedKey, newValues.join(','));
      } else {
        return params.delete(mappedKey);
      }
    }
  });

  return params.toString();
};
